import React, { useEffect, useRef, useCallback } from 'react';
import * as echarts from 'echarts';
import { format, parseISO } from 'date-fns';
import {
  SolarForecastResponse,
  WindOnshoreForecastResponse,
  WindOffshoreForecastResponse,
  LoadForecastResponse,
  ForecastType,
} from '../../../services/powerService';
import { useTheme } from '../../../theme/ThemeContext';
import { useTimezone } from '../../../contexts/useTimezone';

interface ForecastChartProps {
  forecastData:
    | SolarForecastResponse
    | WindOnshoreForecastResponse
    | WindOffshoreForecastResponse
    | LoadForecastResponse;
  forecastType: ForecastType;
}

const ForecastChart: React.FC<ForecastChartProps> = ({ forecastData, forecastType }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const { theme } = useTheme();
  const { timezone, formatInUserTimezone } = useTimezone();
  const isDark = theme === 'dark';

  useEffect(() => {
    // Initialize chart with proper theme
    if (chartRef.current) {
      chartInstance.current = echarts.init(chartRef.current, isDark ? 'dark' : undefined);
    }

    // Handle window resize
    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      chartInstance.current?.dispose();
      window.removeEventListener('resize', handleResize);
    };
  }, [isDark]); // Re-initialize chart when theme changes

  const updateChart = useCallback(() => {
    if (!chartInstance.current || !forecastData || forecastData.data.length === 0) return;

    // Get text color based on theme
    const textColor = isDark ? '#e0e0e0' : '#333';
    const backgroundColor = isDark ? '#1F2937' : '#FFFFFF';

    // Transform data for chart
    const timestamps = forecastData.data.map((item) => {
      if (timezone === 'UTC') {
        return format(parseISO(item.timestamp), 'yyyy-MM-dd HH:mm');
      } else {
        return formatInUserTimezone(item.timestamp, 'yyyy-MM-dd HH:mm');
      }
    });
    const actualValues = forecastData.data.map((item) => item.actual_value);
    const dayAheadForecastValues = forecastData.data.map((item) => item.day_ahead_forecast);
    const intradayForecastValues = forecastData.data.map((item) => item.intraday_forecast || null);
    const currentForecastValues = forecastData.data.map((item) =>
      item.current_forecast !== undefined && item.current_forecast !== null
        ? item.current_forecast
        : null,
    );

    // Get title based on forecast type
    let title = '';
    const yAxisName = 'Power (MW)';
    const colors = ['#E34290', '#4A90E2', '#F5A623', '#2ECC71'];

    switch (forecastType) {
      case 'solar':
        title = 'Solar Power Forecast';
        break;
      case 'wind_onshore':
        title = 'Wind Onshore Power Forecast';
        break;
      case 'wind_offshore':
        title = 'Wind Offshore Power Forecast';
        break;
      case 'load':
        title = 'Load Forecast';
        break;
    }

    // Add TSO info if available
    if (forecastData.tso) {
      title += ` - ${forecastData.tso}`;
    }

    // Add country info
    title += ` (${forecastData.country_code})`;

    // Determine if current forecast exists
    const hasCurrentForecast = currentForecastValues.some((v) => v !== null && v !== undefined);

    // Base legend entries
    const legendEntries = ['Actual', 'Day Ahead Forecast', 'Intraday Forecast'];
    if (hasCurrentForecast) legendEntries.push('Current Forecast');

    // Chart options
    const option: echarts.EChartsOption = {
      backgroundColor: backgroundColor,
      title: {
        text: title,
        left: 'center',
        textStyle: {
          color: textColor,
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985',
          },
        },
      },
      legend: {
        data: legendEntries,
        bottom: 35,
        orient: 'horizontal',
        type: 'scroll',
        textStyle: {
          color: textColor,
        },
        pageButtonPosition: 'end',
        pageButtonGap: 10,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '22%',
        top: '15%',
        containLabel: true,
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          bottom: '2%',
          height: 20,
          borderColor: isDark ? '#374151' : '#E5E7EB',
          textStyle: {
            color: textColor,
          },
          handleStyle: {
            color: isDark ? '#4B5563' : '#9CA3AF',
            borderColor: isDark ? '#6B7280' : '#D1D5DB',
          },
          moveHandleStyle: {
            color: isDark ? '#4B5563' : '#9CA3AF',
          },
          selectedDataBackground: {
            lineStyle: {
              color: isDark ? '#6B7280' : '#D1D5DB',
            },
            areaStyle: {
              color: isDark ? '#4B5563' : '#F3F4F6',
            },
          },
          start: 0,
          end: 100,
          filterMode: 'none', // Don't filter empty values
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
          filterMode: 'none', // Don't filter empty values
        },
      ],
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: timestamps,
        axisLabel: {
          rotate: 45,
          color: textColor,
        },
      },
      yAxis: {
        type: 'value',
        name: yAxisName,
        nameTextStyle: {
          color: textColor,
        },
        axisLabel: {
          color: textColor,
        },
      },
      series: [
        {
          name: 'Actual',
          type: 'line',
          symbol: 'none',
          sampling: 'average',
          itemStyle: {
            color: colors[0],
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: colors[0] + 'cc',
              },
              {
                offset: 1,
                color: colors[0] + '10',
              },
            ]),
          },
          data: actualValues,
        },
        {
          name: 'Day Ahead Forecast',
          type: 'line',
          symbol: 'none',
          sampling: 'average',
          itemStyle: {
            color: colors[1],
          },
          data: dayAheadForecastValues,
        },
        {
          name: 'Intraday Forecast',
          type: 'line',
          symbol: 'none',
          sampling: 'average',
          itemStyle: {
            color: colors[2],
          },
          data: intradayForecastValues,
        },
        // Conditionally add current forecast series
        ...(hasCurrentForecast
          ? [
              {
                name: 'Current Forecast',
                type: 'line',
                symbol: 'none',
                sampling: 'average',
                itemStyle: {
                  color: colors[3],
                },
                data: currentForecastValues,
              },
            ]
          : []),
      ],
    };

    // Draw chart
    chartInstance.current.setOption(option);
  }, [forecastData, forecastType, isDark, timezone, formatInUserTimezone]);

  useEffect(() => {
    updateChart();
  }, [updateChart]); // Only depend on updateChart, which already depends on the other variables

  return (
    <div>
      <div ref={chartRef} style={{ width: '100%', height: '500px' }}></div>
    </div>
  );
};

export default ForecastChart;
